import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Image,
  Alert,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as ImagePicker from 'expo-image-picker';

interface ImageUploaderProps {
  onImageSelected: (imageUri: string) => void;
  existingImages?: string[];
  onRemoveImage?: (index: number) => void;
  maxImages?: number;
  required?: boolean;
  error?: string;
}

const ImageUploader = ({
  onImageSelected,
  existingImages = [],
  onRemoveImage,
  maxImages = 4,
  required = false,
  error,
}: ImageUploaderProps) => {
  const [, setUploading] = useState(false);
  const [uploadingButton, setUploadingButton] = useState<
    'gallery' | 'camera' | null
  >(null);

  const pickImage = async () => {
    if (existingImages.length >= maxImages) {
      Alert.alert(
        'Maximum Images Reached',
        `You can only add up to ${maxImages} images to your moodboard.`
      );
      return;
    }

    try {
      // Request permission
      const { status } =
        await ImagePicker.requestMediaLibraryPermissionsAsync();

      if (status !== 'granted') {
        Alert.alert(
          'Permission Required',
          'Please grant permission to access your photo library.'
        );
        return;
      }

      setUploadingButton('gallery');
      setUploading(true);

      // Launch image picker
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        aspect: [1, 1],
        quality: 0.8,
      });

      // Always clear loading state immediately after picker returns (synchronously)
      setUploadingButton(null);
      setUploading(false);

      // Check if user selected an image
      if (
        result &&
        !result.canceled &&
        result.assets &&
        result.assets.length > 0
      ) {
        onImageSelected(result.assets[0].uri);
      }
      // If canceled, state is already cleared above
    } catch (error) {
      console.error('Error picking image:', error);
      // Always clear state on error
      setUploadingButton(null);
      setUploading(false);
      Alert.alert('Error', 'Failed to select image. Please try again.');
    }
  };

  const takePhoto = async () => {
    if (existingImages.length >= maxImages) {
      Alert.alert(
        'Maximum Images Reached',
        `You can only add up to ${maxImages} images to your moodboard.`
      );
      return;
    }

    try {
      // Request permission
      const { status } = await ImagePicker.requestCameraPermissionsAsync();

      if (status !== 'granted') {
        Alert.alert(
          'Permission Required',
          'Please grant permission to access your camera.'
        );
        return;
      }

      setUploadingButton('camera');
      setUploading(true);

      // Launch camera directly (not folder selection)
      // Note: On web, this will not work, so we check Platform.OS above
      const result = await ImagePicker.launchCameraAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        aspect: [1, 1],
        quality: 0.8,
        allowsMultipleSelection: false,
      });

      // Always clear loading state immediately after camera returns (synchronously)
      setUploadingButton(null);
      setUploading(false);

      // Check if user took a photo
      if (
        result &&
        !result.canceled &&
        result.assets &&
        result.assets.length > 0
      ) {
        onImageSelected(result.assets[0].uri);
      }
      // If canceled, state is already cleared above
    } catch (error) {
      console.error('Error taking photo:', error);
      // Always clear state on error
      setUploadingButton(null);
      setUploading(false);
      Alert.alert('Error', 'Failed to take photo. Please try again.');
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.imagesContainer}>
        {existingImages.map((uri, index) => (
          <View key={index} style={styles.imageWrapper}>
            <Image source={{ uri }} style={styles.image} />
            {onRemoveImage && (
              <TouchableOpacity
                style={styles.removeButton}
                onPress={() => onRemoveImage(index)}
              >
                <Ionicons name="close-circle" size={24} color="#FF4D4F" />
              </TouchableOpacity>
            )}
          </View>
        ))}

        {existingImages.length < maxImages && (
          <View style={styles.buttonsContainer}>
            <TouchableOpacity style={styles.uploadButton} onPress={pickImage}>
              <Ionicons name="images-outline" size={24} color="#7C3AED" />
              <Text style={styles.uploadButtonText}>Gallery</Text>
            </TouchableOpacity>

            {Platform.OS !== 'web' && (
              <TouchableOpacity
                style={styles.uploadButton}
                onPress={takePhoto}
                disabled={uploadingButton === 'camera'}
              >
                <Ionicons
                  name="camera-outline"
                  size={24}
                  color={uploadingButton === 'camera' ? '#9CA3AF' : '#7C3AED'}
                />
                <Text
                  style={[
                    styles.uploadButtonText,
                    uploadingButton === 'camera' && { color: '#9CA3AF' },
                  ]}
                >
                  Camera
                </Text>
              </TouchableOpacity>
            )}
          </View>
        )}
      </View>

      <View style={styles.helperContainer}>
        <Text style={styles.helperText}>
          {existingImages.length} of {maxImages} images added
          {required && existingImages.length === 0 && ' (at least 1 required)'}
        </Text>
        {error && <Text style={styles.errorText}>{error}</Text>}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginVertical: 8,
  },
  imagesContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 8,
  },
  imageWrapper: {
    position: 'relative',
    margin: 4,
  },
  image: {
    width: 100,
    height: 100,
    borderRadius: 12,
    aspectRatio: 1,
  },
  removeButton: {
    position: 'absolute',
    top: -10,
    right: -10,
    backgroundColor: 'white',
    borderRadius: 12,
  },
  buttonsContainer: {
    flexDirection: 'row',
    margin: 4,
  },
  uploadButton: {
    width: 100,
    height: 100,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderStyle: 'dashed',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F9FAFB',
    marginRight: 8,
    aspectRatio: 1,
  },
  uploadButtonText: {
    color: '#7C3AED',
    marginTop: 4,
    fontSize: 12,
  },
  helperText: {
    fontSize: 12,
    color: '#6B7280',
    marginTop: 4,
  },
});

export default ImageUploader;
